import React, { useMemo, useState, useRef, useCallback, useEffect } from 'react'
import {
  Box,
  Button,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Text,
  VStack,
  Checkbox,
  Badge,
  Icon,
  useOutsideClick,
  Tag,
  TagLabel,
  TagCloseButton,
  Wrap,
  WrapItem,
  IconButton,
} from '@chakra-ui/react'
import { FaChevronDown, FaSearch, FaTimes, FaPlus } from 'react-icons/fa'

interface ExchangeSymbolCascaderProps {
  groupedData: Record<string, string[]>
  selectedValues: string[]
  onChange: (values: string[]) => void
  placeholder?: string
  isInvalid?: boolean
  isMulti?: boolean 
}

const SymbolItem = React.memo(({ 
  symbol, 
  exchange, 
  isChecked, 
  isMulti, 
  onToggle,
  isCustom = false
}: { 
  symbol: string, 
  exchange: string, 
  isChecked: boolean, 
  isMulti: boolean,
  onToggle: (exchange: string, symbol: string) => void,
  isCustom?: boolean
}) => {
  return (
    <Flex
      as="label"
      cursor="pointer"
      px={3}
      py={2}
      borderRadius="md"
      _hover={{ bg: 'gray.50' }}
      align="center"
      onClick={(e) => {
        // Usually default label click is fine for checkbox, but for custom/single we handle manually
        if (!isMulti || isCustom) {
          e.preventDefault()
          onToggle(exchange, symbol)
        }
      }}
    >
      {isMulti ? (
        <Checkbox
          isChecked={isChecked}
          onChange={() => onToggle(exchange, symbol)}
          colorScheme="blue"
          mr={3}
          pointerEvents="none"
        />
      ) : (
        <Box mr={3} w="16px" h="16px" display="flex" alignItems="center" justifyContent="center">
            {isChecked && <Box w="8px" h="8px" bg="blue.500" borderRadius="full" />}
            {isCustom && !isChecked && <Icon as={FaPlus} color="gray.400" w={3} h={3} />}
        </Box>
      )}
      <Text fontSize="sm" fontWeight={isChecked ? "semibold" : "normal"}>
        {isCustom ? `Use "${symbol}"` : symbol}
      </Text>
    </Flex>
  )
})

// Memoized Multi-select Trigger Component
const MultiSelectTrigger = React.memo(({
  selectedValues,
  placeholder,
  isInvalid,
  isOpen,
  onOpen,
  onRemove,
  inputValue,
  inputRef,
  onInputChange,
  onInputKeyDown,
}: {
  selectedValues: string[]
  placeholder: string
  isInvalid: boolean
  isOpen: boolean
  onOpen: () => void
  onRemove: (value: string) => void
  inputValue: string
  inputRef: React.RefObject<HTMLInputElement>
  onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onInputKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void
}) => (
  <Box
    w="100%"
    borderWidth="1px"
    borderRadius="md"
    borderColor={isInvalid ? 'red.500' : isOpen ? 'blue.400' : 'inherit'}
    boxShadow={isOpen ? '0 0 0 1px var(--chakra-colors-blue-400)' : undefined}
    minH="40px"
    px={2}
    py={1.5}
    onClick={() => {
      onOpen()
      inputRef.current?.focus()
    }}
    cursor="text"
    transition="border-color 0.2s, box-shadow 0.2s"
  >
    <Wrap spacing={1} align="center">
      {selectedValues.length > 0 ? (
        selectedValues.map(val => (
          <WrapItem key={val}>
            <Tag size="sm" borderRadius="full" variant="solid" colorScheme="blue">
              <TagLabel>{val}</TagLabel>
              <TagCloseButton onMouseDown={(e) => {
                e.preventDefault()
                e.stopPropagation()
                onRemove(val)
              }} />
            </Tag>
          </WrapItem>
        ))
      ) : (
        <></>
      )}
      <WrapItem flex="1" minW="180px">
        <Input
          ref={inputRef}
          variant="unstyled"
          value={inputValue}
          placeholder={selectedValues.length === 0 ? placeholder : ''}
          onChange={onInputChange}
          onKeyDown={onInputKeyDown}
          onMouseDown={onOpen}
          onFocus={onOpen}
          onClick={onOpen}
        />
      </WrapItem>
      <WrapItem>
        <Icon as={FaChevronDown} color="gray.500" fontSize="sm" />
      </WrapItem>
    </Wrap>
  </Box>
))

// Memoized Single-select Trigger Component
const SingleSelectTrigger = React.memo(({
  inputValue,
  placeholder,
  isInvalid,
  onOpen,
  onChange,
  onKeyDown,
  onBlur,
  onClear,
}: {
  inputValue: string
  placeholder: string
  isInvalid: boolean
  onOpen: () => void
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onKeyDown: (e: React.KeyboardEvent) => void
  onBlur: () => void
  onClear: () => void
}) => (
  <InputGroup>
    <Input
      value={inputValue}
      onChange={onChange}
      onKeyDown={onKeyDown}
      onMouseDown={onOpen}
      onClick={onOpen}
      onFocus={onOpen}
      onBlur={onBlur}
      placeholder={placeholder}
      borderColor={isInvalid ? 'red.500' : undefined}
      _focus={{ borderColor: 'blue.400' }}
    />
    <InputRightElement>
      {inputValue ? (
        <IconButton
          aria-label="Clear"
          icon={<FaTimes />}
          size="xs"
          variant="ghost"
          onMouseDown={(e) => {
            e.preventDefault()
            onClear()
          }}
        />
      ) : (
        <Icon as={FaChevronDown} color="gray.500" fontSize="sm" />
      )}
    </InputRightElement>
  </InputGroup>
))

export const ExchangeSymbolCascader: React.FC<ExchangeSymbolCascaderProps> = ({
  groupedData,
  selectedValues,
  onChange,
  placeholder = "Select assets",
  isInvalid = false,
  isMulti = true,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [activeExchange, setActiveExchange] = useState<string>('')
  const [searchQuery, setSearchQuery] = useState('')
  const [inputValue, setInputValue] = useState('')
  const [multiInputValue, setMultiInputValue] = useState('')
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerInputRef = useRef<HTMLInputElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  const onOpen = useCallback(() => setIsOpen(true), [])
  const onClose = useCallback(() => setIsOpen(false), [])

  useOutsideClick({
    ref: rootRef,
    handler: onClose,
  })

  const exchanges = useMemo(() => Object.keys(groupedData).sort(), [groupedData])

  // Initial active exchange
  useEffect(() => {
    if (!activeExchange && exchanges.length > 0) {
      setActiveExchange(exchanges[0])
    }
  }, [exchanges, activeExchange])

  // Sync inputValue with selectedValues in Single Mode
  useEffect(() => {
    if (!isMulti) {
      setInputValue(selectedValues.length > 0 ? selectedValues[0] : '')
    }
  }, [selectedValues, isMulti])

  // Filter symbols
  const filteredSymbols = useMemo(() => {
    if (!activeExchange) return []
    const symbols = groupedData[activeExchange] || []
    if (!searchQuery) return symbols
    return symbols.filter(s => s.toLowerCase().includes(searchQuery.toLowerCase()))
  }, [activeExchange, groupedData, searchQuery])

  const selectedSet = useMemo(() => new Set(selectedValues), [selectedValues])

  const handleToggle = useCallback((exchange: string, symbol: string) => {
    const fullValue = `${exchange}_${symbol}`
    
    if (!isMulti) {
      onChange([fullValue])
      setInputValue(fullValue) // Update display immediately with full value
      onClose()
      return
    }

    if (selectedSet.has(fullValue)) {
      onChange(selectedValues.filter(v => v !== fullValue))
    } else {
      onChange([...selectedValues, fullValue])
    }
  }, [isMulti, onChange, onClose, selectedSet, selectedValues])

  const handleRemoveValue = useCallback((value: string) => {
    onChange(selectedValues.filter(v => v !== value))
  }, [onChange, selectedValues])

  const commitMultiInputValue = useCallback((rawValue: string) => {
    const values = rawValue
      .split(',')
      .map(v => v.trim())
      .filter(Boolean)
    if (values.length === 0) return

    const nextValues = [...selectedValues]
    values.forEach((value) => {
      if (!nextValues.includes(value)) {
        nextValues.push(value)
      }
    })

    if (nextValues.length !== selectedValues.length) {
      onChange(nextValues)
    }
    setMultiInputValue('')
  }, [onChange, selectedValues])

  const handleMultiInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setMultiInputValue(e.target.value)
    onOpen()
  }, [onOpen])

  const handleMultiInputKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      commitMultiInputValue(multiInputValue)
    }
  }, [commitMultiInputValue, multiInputValue])

  const handleClearAll = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation()
    onChange([])
    setInputValue('')
  }, [onChange])

  // Handle main input changes
  const handleMainInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    setInputValue(val)
    onOpen()
  }, [onOpen])

  // Handle Enter in main input
  const handleMainInputKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      // If input is empty, maybe clear?
      if (!inputValue.trim()) {
        onChange([])
        return
      }
      // Commit current input as value directly (no exchange prefix)
      onChange([inputValue.trim()])
      onClose()
    }
  }, [inputValue, onChange, onClose])

  // Handle blur on main input - commit the value when user clicks away
  const handleMainInputBlur = useCallback(() => {
    // Only commit if there's a value and it's different from current selection
    if (inputValue.trim()) {
      onChange([inputValue.trim()])
    }
  }, [inputValue, onChange])

  const selectedCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    selectedValues.forEach(val => {
      const separatorIndex = val.indexOf('_');
      const ex = separatorIndex !== -1 ? val.substring(0, separatorIndex) : val;
      counts[ex] = (counts[ex] || 0) + 1
    })
    return counts
  }, [selectedValues])

  return (
    <Box ref={rootRef} w="100%" position="relative">
      <Box w="100%">
        {isMulti ? (
          <MultiSelectTrigger
            selectedValues={selectedValues}
            placeholder={placeholder}
            isInvalid={isInvalid}
            isOpen={isOpen}
            onOpen={onOpen}
            onRemove={handleRemoveValue}
            inputValue={multiInputValue}
            inputRef={triggerInputRef}
            onInputChange={handleMultiInputChange}
            onInputKeyDown={handleMultiInputKeyDown}
          />
        ) : (
          <SingleSelectTrigger
            inputValue={inputValue}
            placeholder={placeholder}
            isInvalid={isInvalid}
            onOpen={onOpen}
            onChange={handleMainInputChange}
            onKeyDown={handleMainInputKeyDown}
            onBlur={handleMainInputBlur}
            onClear={handleClearAll}
          />
        )}
      </Box>

      {isOpen && (
        <Box
          position="absolute"
          top="calc(100% + 4px)"
          left={0}
          right={0}
          zIndex={1600}
          bg="white"
          borderWidth="1px"
          borderRadius="md"
          boxShadow="xl"
          overflow="hidden"
        >
          <Flex h="320px">
            {/* Left Column: Exchanges */}
            <Box w="35%" borderRightWidth="1px" bg="gray.50" overflowY="auto" py={2}>
              {exchanges.map(ex => (
                <Flex
                  key={ex}
                  px={3}
                  py={3}
                  cursor="pointer"
                  bg={activeExchange === ex ? 'white' : 'transparent'}
                  borderLeftWidth="3px"
                  borderLeftColor={activeExchange === ex ? 'blue.500' : 'transparent'}
                  _hover={{ bg: activeExchange === ex ? 'white' : 'gray.100' }}
                  onMouseDown={(e) => {
                    e.preventDefault()
                    setActiveExchange(ex)
                    setSearchQuery('')
                  }}
                  justify="space-between"
                  align="center"
                >
                  <Text fontWeight={activeExchange === ex ? 'bold' : 'normal'} fontSize="sm">
                    {ex}
                  </Text>
                  {selectedCounts[ex] > 0 && (
                    <Badge colorScheme="blue" borderRadius="full" fontSize="xs">
                      {selectedCounts[ex]}
                    </Badge>
                  )}
                </Flex>
              ))}
            </Box>

            {/* Right Column: Symbols */}
            <Box w="65%" display="flex" flexDirection="column">
              <Box p={3} borderBottomWidth="1px">
                <InputGroup size="sm">
                  <InputLeftElement pointerEvents="none">
                    <Icon as={FaSearch} color="gray.400" />
                  </InputLeftElement>
                  <Input
                    ref={searchInputRef}
                    placeholder={`Filter ${activeExchange}...`}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    borderRadius="md"
                    onFocus={onOpen}
                  />
                </InputGroup>
                <Text fontSize="xs" color="gray.500" mt={2}>
                  {filteredSymbols.length} symbols
                </Text>
              </Box>

              {/* Symbol List */}
              <Box flex="1" overflowY="auto" p={2}>
                <VStack align="stretch" spacing={0}>
                  {filteredSymbols.length > 0 ? (
                    filteredSymbols.map(sym => (
                      <SymbolItem
                        key={sym}
                        symbol={sym}
                        exchange={activeExchange}
                        isChecked={selectedSet.has(`${activeExchange}_${sym}`)}
                        isMulti={isMulti}
                        onToggle={handleToggle}
                      />
                    ))
                  ) : (
                    <Flex justify="center" align="center" h="100%" color="gray.400">
                      No options
                    </Flex>
                  )}
                </VStack>
              </Box>
            </Box>
          </Flex>

          {/* Footer Actions - Only shown if isMulti is true */}
          {isMulti && (
            <Box p={3} borderTopWidth="1px" bg="gray.50">
              <Flex justify="space-between" align="center">
                <Button
                  size="sm"
                  variant="ghost"
                  colorScheme="red"
                  onMouseDown={(e) => {
                    e.preventDefault()
                    onChange([])
                  }}
                  visibility={selectedValues.length > 0 ? 'visible' : 'hidden'}
                >
                  Clear All
                </Button>
                <Button
                  size="sm"
                  colorScheme="blue"
                  onMouseDown={(e) => {
                    e.preventDefault()
                    onClose()
                    triggerInputRef.current?.blur()
                  }}
                >
                  Done
                </Button>
              </Flex>
            </Box>
          )}
        </Box>
      )}
    </Box>
  )
}
